import {
  IsAnImageError,
  parseMediaOnWebWorker,
} from '@remotion/media-parser/worker';
import { PlayerRef } from '@remotion/player';
import { DropPosition } from '../assets/add-asset';
import { EditorStarterAsset } from '../assets/assets';
import { cacheAssetLocally } from '../caching/indexeddb';
import { setLocalUrl } from '../caching/load-to-blob-url';
import { generateRandomId } from '../utils/generate-random-id';
import { getSvgDimensions } from '../utils/get-svg-dimensions';
import { makeAudioItem } from './audio/make-audio-item';
import { makeGifItem } from './gif/make-gif-item';
import { makeImageItem } from './image/make-image-item';
import { EditorStarterItem } from './item-type';
import { makeVideoItem } from './video/make-video-item';
import { makeWebpItem } from './webp/make-webp-item';
import { isAnimatedWebp } from './webp/get-webp-duration';
import {makePdfItem} from '@waka/compat/make-pdf-item';

export const makeItem = async ({
  file,
  playerRef,
  dropPosition,
  fps,
  compositionWidth,
  compositionHeight,
  filename,
  remoteUrl,
  remoteFileKey,
}: {
  file: Blob;
  playerRef: React.RefObject<PlayerRef | null>;
  dropPosition: DropPosition | null;
  fps: number;
  compositionWidth: number;
  compositionHeight: number;
  remoteUrl: string | null;
  remoteFileKey: string | null;
  filename: string;
}): Promise<{ item: EditorStarterItem; asset: EditorStarterAsset }> => {
  if(file.type.toLowerCase()==='application/pdf')return makePdfItem({file,filename,remoteUrl,remoteFileKey,fps,compositionWidth,compositionHeight,currentFrame:playerRef.current?.getCurrentFrame()??0,dropPosition});
  const assetId = generateRandomId();
  const url = URL.createObjectURL(file);
  const fileType = file.type.toLowerCase();
  const lowerFilename = filename.toLowerCase();
  const isGifFile = fileType === 'image/gif' || lowerFilename.endsWith('.gif');
  const isWebpFile =
    fileType === 'image/webp' || lowerFilename.endsWith('.webp');
  setLocalUrl(assetId, url);

  await cacheAssetLocally({
    assetId: assetId,
    value: file,
  });

  try {
    // Raster metadata uses native image decoding, independent of the video worker's image-exception path.
    if (['image/png','image/jpeg','image/gif','image/webp'].includes(fileType)) {
      const dimensions = await getImageDimensionsFromBlobUrl(url);
      const common = {file,fps,compositionWidth,compositionHeight,currentFrame:playerRef.current?.getCurrentFrame()??0,
        dropPosition,dimensions,assetId,filename,remoteUrl,remoteFileKey};
      if(isGifFile)return await makeGifItem({...common,blobUrl:url});
      if(isWebpFile && await isAnimatedWebp(file))return await makeWebpItem(common);
      return await makeImageItem(common);
    }
    const metadata = await parseMediaOnWebWorker({
      src: file,
      fields: {
        slowDurationInSeconds: true,
        dimensions: true,
        videoCodec: true,
        audioCodec: true,
      },
      acknowledgeRemotionLicense: true,
    });

    if (isGifFile) {
      const dimensions =
        metadata.dimensions ?? (await getImageDimensionsFromBlobUrl(url));
      const { item, asset } = await makeGifItem({
        file,
        fps,
        compositionWidth,
        compositionHeight,
        currentFrame: playerRef.current?.getCurrentFrame() ?? 0,
        dropPosition,
        dimensions,
        assetId,
        blobUrl: url,
        filename,
        remoteUrl,
        remoteFileKey,
      });
      return { item, asset };
    }

    if (isWebpFile && (await isAnimatedWebp(file))) {
      const dimensions =
        metadata.dimensions ?? (await getImageDimensionsFromBlobUrl(url));
      const { item, asset } = await makeWebpItem({
        file,
        fps,
        compositionWidth,
        compositionHeight,
        currentFrame: playerRef.current?.getCurrentFrame() ?? 0,
        dropPosition,
        dimensions,
        assetId,
        filename,
        remoteUrl,
        remoteFileKey,
      });
      return { item, asset };
    }

    if (metadata.videoCodec === null && metadata.audioCodec) {
      const { item, asset } = makeAudioItem({
        file,
        fps,
        durationInSeconds: metadata.slowDurationInSeconds,
        currentFrame: playerRef.current?.getCurrentFrame() ?? 0,
        size: file.size,
        assetId,
        filename,
        remoteUrl,
        remoteFileKey,
      });

      return { item, asset };
    }

    if (!metadata.dimensions) {
      throw new Error('cannot get video dimensions');
    }

    const { item, asset } = makeVideoItem({
      file,
      fps,
      compositionWidth,
      compositionHeight,
      durationInSeconds: metadata.slowDurationInSeconds,
      dimensions: metadata.dimensions,
      currentFrame: playerRef.current?.getCurrentFrame() ?? 0,
      dropPosition,
      assetId,
      hasAudioTrack: metadata.audioCodec !== null,
      filename,
      remoteUrl,
      remoteFileKey,
    });

    return { item, asset };
  } catch (error) {
    /// 여기를 타야하는데 안탐
    if (error instanceof IsAnImageError) {
      if (error.imageType === 'gif' || isGifFile) {
        const dimensions =
          error.dimensions ?? (await getImageDimensionsFromBlobUrl(url));
        const { item, asset } = await makeGifItem({
          file,
          fps,
          compositionWidth,
          compositionHeight,
          currentFrame: playerRef.current?.getCurrentFrame() ?? 0,
          dropPosition,
          dimensions,
          assetId,
          blobUrl: url,
          filename,
          remoteUrl,
          remoteFileKey,
        });
        return { item, asset };
      }

      if (isWebpFile && (await isAnimatedWebp(file))) {
        const dimensions =
          error.dimensions ?? (await getImageDimensionsFromBlobUrl(url));
        const { item, asset } = await makeWebpItem({
          file,
          fps,
          compositionWidth,
          compositionHeight,
          currentFrame: playerRef.current?.getCurrentFrame() ?? 0,
          dropPosition,
          dimensions,
          assetId,
          filename,
          remoteUrl,
          remoteFileKey,
        });
        return { item, asset };
      }

      if (!error.dimensions) {
        throw new Error('Could not get dimensions for image');
      }

      const { item, asset } = await makeImageItem({
        file,
        fps,
        compositionWidth,
        compositionHeight,
        currentFrame: playerRef.current?.getCurrentFrame() ?? 0,
        dropPosition,
        assetId,
        filename,
        remoteUrl,
        remoteFileKey,
        dimensions: error.dimensions,
      });

      return { item, asset };
    }

    if (file.type === 'image/svg+xml') {
      // svg 처리
      // svg 크기 가져오기
      const svgText = await file.text();
      const dimensions = getSvgDimensions(svgText);
      const { item, asset } = await makeImageItem({
        file,
        fps,
        compositionWidth,
        compositionHeight,
        currentFrame: playerRef.current?.getCurrentFrame() ?? 0,
        dropPosition,
        dimensions,
        assetId,
        filename,
        remoteUrl,
        remoteFileKey,
      });

      return { item, asset };
    }

    if(import.meta.env.DEV)console.warn('[native-media-probe]',error instanceof Error?`${error.name}: ${error.message}`.slice(0,240):String(error).slice(0,240));
    throw new Error('원본 미디어의 재생 정보를 읽지 못했습니다.');
  }
};

const getImageDimensionsFromBlobUrl = (
  blobUrl: string,
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Could not load image dimensions'));
    img.src = blobUrl;
  });
};
